import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { normalizeInspectionSettingsBundle } from '../lib/inspectionSettings';

const InspectionSettingsContext = createContext(null);
const INSPECTION_SETTINGS_URL = '/api/admin/inspection-settings';
const READ_ERROR = 'دریافت تنظیمات بازرسی ناموفق بود.';
const SAVE_ERROR = 'ذخیره تنظیمات بازرسی ناموفق بود.';
const SAVE_CONFLICT_ERROR = 'تنظیمات بازرسی توسط مدیر دیگری تغییر کرده است. تازه‌ترین نسخه دریافت شد؛ تغییرات را دوباره بررسی کنید.';

class InspectionSettingsRequestError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function requestInspectionSettings(method, body, revision) {
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (!accessToken) throw new Error('نشست ادمین در دسترس نیست.');

  const options = {
    headers: { Authorization: `Bearer ${accessToken}` },
  };
  if (method === 'PUT') {
    if (typeof revision !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(revision)) {
      throw new InspectionSettingsRequestError(SAVE_ERROR, 428);
    }
    options.method = 'PUT';
    options.headers['Content-Type'] = 'application/json';
    options.headers['If-Match'] = `"${revision}"`;
    options.body = JSON.stringify(body);
  }

  const response = await fetch(INSPECTION_SETTINGS_URL, options);
  let payload;
  try {
    payload = await response.json();
  } catch (_error) {
    throw new Error(method === 'PUT' ? SAVE_ERROR : READ_ERROR);
  }
  if (!response.ok) {
    throw new InspectionSettingsRequestError(
      typeof payload?.error === 'string'
        ? payload.error
        : (method === 'PUT' ? SAVE_ERROR : READ_ERROR),
      response.status,
    );
  }
  return payload;
}

function normalizeInspectionSettingsResponse(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Inspection settings response is malformed');
  }
  const { revision, ...bundle } = payload;
  if (typeof revision !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(revision)) {
    throw new Error('Inspection settings revision is malformed');
  }
  return {
    settings: normalizeInspectionSettingsBundle(bundle),
    revision,
  };
}

function withCause(message, cause) {
  const error = new Error(message);
  error.cause = cause;
  if (cause?.status === 409) error.code = 'INSPECTION_SETTINGS_CONFLICT';
  return error;
}

export function InspectionSettingsProvider({ children }) {
  const [effectiveSettings, setEffectiveSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const activeOperationCountRef = useRef(0);
  const latestOperationRef = useRef(0);
  const revisionRef = useRef(null);

  const beginOperation = useCallback(() => {
    latestOperationRef.current += 1;
    activeOperationCountRef.current += 1;
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }
    return latestOperationRef.current;
  }, []);

  const finishOperation = useCallback(() => {
    activeOperationCountRef.current -= 1;
    if (mountedRef.current) {
      setLoading(activeOperationCountRef.current > 0);
    }
  }, []);

  const readEffectiveSettings = useCallback(async (operation) => {
    try {
      const response = await requestInspectionSettings('GET');
      const normalized = normalizeInspectionSettingsResponse(response);
      if (mountedRef.current && operation === latestOperationRef.current) {
        setEffectiveSettings(normalized.settings);
        revisionRef.current = normalized.revision;
        setError(null);
      }
      return normalized.settings;
    } catch (cause) {
      if (mountedRef.current && operation === latestOperationRef.current) {
        revisionRef.current = null;
        setError(READ_ERROR);
      }
      throw withCause(READ_ERROR, cause);
    }
  }, []);

  const refresh = useCallback(async () => {
    const operation = beginOperation();
    try {
      return await readEffectiveSettings(operation);
    } finally {
      finishOperation();
    }
  }, [beginOperation, finishOperation, readEffectiveSettings]);

  const saveInspectionSettings = useCallback(async (draft) => {
    // Validate and detach the request without ever promoting panel draft state
    // into the shared persisted state.
    const requestBundle = normalizeInspectionSettingsBundle(draft);
    const expectedRevision = revisionRef.current;
    const operation = beginOperation();

    try {
      const response = await requestInspectionSettings('PUT', requestBundle, expectedRevision);
      const normalized = normalizeInspectionSettingsResponse(response);
      if (mountedRef.current && operation === latestOperationRef.current) {
        setEffectiveSettings(normalized.settings);
        revisionRef.current = normalized.revision;
        setError(null);
      }
      return normalized.settings;
    } catch (cause) {
      // A failed response or network interruption can leave persistence
      // ambiguous. Reconcile with the server before the save promise settles.
      try {
        // Recovery belongs to this save's single logical operation. It must not
        // increment or finish the public-operation counter a second time.
        await readEffectiveSettings(operation);
      } catch (_refreshError) {
        // The current generation owns fail-closed read error state; preserve
        // the save failure as the rejection presented to the caller.
      }
      throw withCause(cause?.status === 409 ? SAVE_CONFLICT_ERROR : SAVE_ERROR, cause);
    } finally {
      finishOperation();
    }
  }, [beginOperation, finishOperation, readEffectiveSettings]);

  useEffect(() => {
    mountedRef.current = true;
    refresh().catch(() => {});
    return () => { mountedRef.current = false; };
  }, [refresh]);

  const concealModelIdentities = loading
    || Boolean(error)
    || effectiveSettings?.concealModelIdentities !== false;
  const value = useMemo(() => ({
    effectiveSettings,
    concealModelIdentities,
    loading,
    error,
    refresh,
    saveInspectionSettings,
  }), [
    concealModelIdentities,
    effectiveSettings,
    error,
    loading,
    refresh,
    saveInspectionSettings,
  ]);

  return (
    <InspectionSettingsContext.Provider value={value}>
      {children}
    </InspectionSettingsContext.Provider>
  );
}

export function useInspectionSettings() {
  const context = useContext(InspectionSettingsContext);
  if (!context) {
    throw new Error('useInspectionSettings must be used within InspectionSettingsProvider');
  }
  return context;
}
